// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
//
// The review screen's shell: data, selection and the wiring between the two
// columns. The left column is `./AskV2ReviewLeftColumn.tsx`, the artwork
// column is `./AskV2ReviewArtworkColumn.tsx` (its viewers are
// `./AskV2ReviewCompareLayout.tsx`), the comment-chip effects are
// `./useAskV2ReviewCommentContext.ts` and confirm/reject is
// `./useAskV2ReviewJudge.ts`. Add new behavior there, not here. The pin
// grouping (spec dave.savostyanov/0090) lives in `./pinGroups.ts`,
// `./useAskV2ReviewPinView.ts` and `./useAskV2ReviewGroupPins.ts`; this module
// only wires them.
import { CircleNotch } from "@phosphor-icons/react";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewArtworkColumn } from "./AskV2ReviewArtworkColumn";
import { AskV2ReviewLeftColumn } from "./AskV2ReviewLeftColumn";
import { AskV2ReviewPanes } from "./AskV2ReviewPanes";
import { AskV2ReviewPinViewControls } from "./AskV2ReviewPinViewControls";
import {
  rememberedReviewCommentId,
  useRememberReviewCommentSelection,
} from "./reviewCommentsPlace";
import {
  type CommentWithOptionalTags,
  savedReviewCommentFilter,
} from "./reviewCommentTags";
import { useAskPunttReview } from "./useAskPunttReview";
import { useAskV2ReviewCommentContext } from "./useAskV2ReviewCommentContext";
import { useAskV2ReviewCommentFilter } from "./useAskV2ReviewCommentFilter";
import { useAskV2ReviewGroupPins } from "./useAskV2ReviewGroupPins";
import { useAskV2ReviewHumanComment } from "./useAskV2ReviewHumanComment";
import { useAskV2ReviewJudge } from "./useAskV2ReviewJudge";
import { useAskV2ReviewPanelComments } from "./useAskV2ReviewPanelComments";
import { useAskV2ReviewPinView } from "./useAskV2ReviewPinView";
import { useAskV2ReviewVersionCompare } from "./useAskV2ReviewVersionCompare";
import { useAskV2ReviewVersionMarks } from "./useAskV2ReviewVersionMarks";
import { useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";
import { usePersistReviewCommentFilter } from "./usePersistReviewCommentFilter";
import { type ReviewVersionOption } from "./VersionPicker";

import {
  type AskPunttArtifactContextValue,
  useReviewArtworkFocus,
} from "../../components/ask-puntt-review";
import { mapReviewFilesToAssets } from "../../components/ask-puntt-review/mapReviewAssets";
import { type AskV2TransportApi } from "../transport/useAskV2Transport";

type FeedMember = {
  _id: string;
  name?: string;
  email?: string;
  avatar?: string | null;
};

type AskV2ReviewWorkspaceProps = {
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  transport: AskV2TransportApi;
  versionOptions?: readonly ReviewVersionOption[];
  feedPresentation?: "chat" | "feed";
  feedActorName?: string;
  feedActorAvatar?: string;
  feedMembers?: FeedMember[];
  currentUserId?: string;
  authorNameByUserId?: Readonly<Record<string, string>>;
  artifactContext?: AskPunttArtifactContextValue;
  /** Chat composer pinned under the left findings column only. */
  composer?: ReactNode;
  /**
   * The viewer may not write to the chat behind this review. The transport the
   * page hands us is already gated, but three of this workspace's writes never
   * ride `AskV2TransportApi`, so the gate cannot reach them and each needs the
   * prop: the feed thread's per-row thumbs (`submitFeedback`), the artwork
   * pane's human-comment composer (`handleSubmitHumanComment` →
   * `createReviewComment`) and the findings-list judge controls
   * (`handleJudgeComment` → `updateReviewComment`). All three are withheld
   * here, and so is Edit on the viewer's own comments (the left column's
   * `handleEditComment`), which christo does gate: only a human comment's
   * author may change its text.
   *
   * Defence in depth, not authorization: christo authorizes the two review
   * comment routes on enterprise scope alone (see the scope note on
   * `canInteract` in AskV2ChatPage.tsx), so those POSTs would still succeed if
   * a caller reached them another way. Making the read-only posture real needs
   * a membership check inside `createReviewComment` / `updateReviewComment`.
   */
  readOnly?: boolean;
};

// why: one identity while the review loads, so the filter's memos do not
// recompute on every render of a workspace that has no comments yet.
const EMPTY_COMMENTS: readonly CommentWithOptionalTags[] = [];

export function AskV2ReviewWorkspace({
  workspace,
  transport,
  versionOptions = [],
  feedPresentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedMembers,
  currentUserId,
  authorNameByUserId,
  artifactContext,
  composer,
  readOnly = false,
}: AskV2ReviewWorkspaceProps) {
  const { t } = useTranslation("ui");
  const reviewQuery = useAskPunttReview(workspace.reviewId);
  const review = reviewQuery.data?.review;
  const persistCommentFilter = usePersistReviewCommentFilter(review?.reviewId);
  const artworkFiles = useMemo(
    () => mapReviewFilesToAssets(review?.files ?? []),
    [review?.files],
  );
  const contextFiles = useMemo(
    () => mapReviewFilesToAssets(review?.contextAssets ?? []),
    [review?.contextAssets],
  );

  // why: syncing comment selection to the /comments/:id route remounts the page
  // and resets the findings list scroll position; keep selection local while open.
  const artworkFocus = useReviewArtworkFocus({
    comments: review?.comments,
    initialCommentId:
      workspace.commentId ?? rememberedReviewCommentId(workspace.reviewId),
  });
  useRememberReviewCommentSelection(
    workspace.reviewId,
    review != null,
    artworkFocus.selectedCommentId,
  );

  const commentFilter = useAskV2ReviewCommentFilter({
    comments: review?.comments ?? EMPTY_COMMENTS,
    authorNameByUserId,
    reviewId: review?.reviewId,
    viewerUserId: currentUserId,
    startedBy: review?.startedBy,
    selectedReviewerIds: review?.selectedReviewerIds,
    selectedExpertIds: review?.selectedExpertIds,
    savedFilter: savedReviewCommentFilter(review?.commentFilter),
    onSaveFilter: persistCommentFilter,
  });
  const { isVisible: isCommentFilterVisible } = commentFilter;

  const { panelComments, loadedGroups } = useAskV2ReviewPanelComments({
    comments: review?.comments,
    reviewId: review?.reviewId,
    isCommentFilterVisible,
  });

  const {
    selectedCommentId,
    selectComment,
    clearComment,
    artworkPaneRef,
    artworkHeaderControls,
    setArtworkHeaderControls,
  } = artworkFocus;

  // The pins as she chose to see them: all, or the open findings in groups,
  // minor and check findings shown or not (spec dave.savostyanov/0090).
  const pinView = useAskV2ReviewPinView({
    comments: panelComments,
    loadedGroups,
    summaries: review?.groupSummaries,
    selectedCommentId,
  });

  // why: numbered once over the findings the list shows, in its order (group by
  // group when grouped), and handed to the list and the pins alike, so a card
  // and its pin carry one number.
  const commentNumbers = useMemo(() => {
    const numbers: Record<string, number> = {};
    pinView.ordered.forEach((comment, index) => {
      numbers[comment._id] = index + 1;
    });
    return numbers;
  }, [pinView.ordered]);
  // A finding the minor switch hides cannot stay selected; a companion stays, inside its lead's card.
  const shownComments = useMemo(
    () => [
      ...pinView.ordered,
      ...Array.from(pinView.companionsOf.values()).flat(),
    ],
    [pinView.companionsOf, pinView.ordered],
  );

  const { setLeftPane } = workspace;
  const { removeCommentContext } = transport;
  const handleRemoveCommentContext = useCallback(
    (id: string) => {
      removeCommentContext(id);
      clearComment();
    },
    [clearComment, removeCommentContext],
  );
  const handleSubmitHumanComment = useAskV2ReviewHumanComment({
    reviewId: workspace.reviewId,
    comments: review?.comments,
    setLeftPane,
    selectComment,
  });

  const { commentContextFor } = useAskV2ReviewCommentContext({
    review,
    workspace,
    transport,
    selectedCommentId,
    panelComments: shownComments,
    commentNumbers,
    isCommentFilterVisible,
    clearComment,
  });

  const { judgmentFailed, isJudgmentPending, handleJudgeComment } =
    useAskV2ReviewJudge({
      reviewId: workspace.reviewId,
      panelComments: pinView.ordered,
      selectComment,
      clearComment,
      commentContextFor,
      transport,
    });

  const { resolvedVersionOptions, versionMarkOf } = useAskV2ReviewVersionMarks(
    versionOptions,
    review,
  );

  // SPIKE (side-by-side version compare): another review of this chat shown
  // beside this one. The findings list steps aside while it is open.
  const compare = useAskV2ReviewVersionCompare({
    review,
    versionOptions: resolvedVersionOptions,
    selectedCommentId,
    historyMessages: transport.historyMessages ?? [],
    panelComments: pinView.ordered,
    commentNumbers,
  });
  const { isComparing } = compare;

  const groupPins = useAskV2ReviewGroupPins({
    pinView,
    isComparing,
    commentNumbers,
    selectedCommentId,
    clearComment,
  });
  // A link from the chat's version check card opens the comparison. why wait
  // for canCompare: the versions list loads after the review, and taking the
  // request before it does would drop it with no comparison to open.
  const { compareRequested, consumeCompareRequest } = workspace;
  const { canCompare, setComparing } = compare;
  useEffect(() => {
    if (!compareRequested || !canCompare) return;
    setComparing(true);
    consumeCompareRequest();
  }, [canCompare, compareRequested, consumeCompareRequest, setComparing]);

  if (reviewQuery.isLoading) {
    return (
      <div
        className="flex flex-1 items-center justify-center"
        data-testid="ask-v2-review-loading"
      >
        <CircleNotch
          size={24}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (reviewQuery.isError || !review) {
    return (
      <div
        className="flex flex-1 items-center justify-center px-6 text-center text-sm text-puntt-neutral-gray-10"
        data-testid="ask-v2-review-error"
      >
        {t("askV2.review.loadError")}
      </div>
    );
  }

  return (
    <div
      className="flex size-full min-h-0 flex-1 overflow-hidden"
      data-testid="ask-v2-review-workspace"
      // why: a `<div>` with no role is `role="generic"`, and ARIA prohibits
      // `aria-label` there (axe `aria-prohibited-attr`) -- the name was
      // computed and then dropped, so both halves of this screen reached a
      // reader as unnamed generic boxes and the artwork pane below could not
      // be told apart from the findings column. The two names this component
      // already pays for only exist once something owns them; `region` is what
      // the findings list itself uses (`<section aria-label>` in
      // AskV2ReviewCommentsList) and what CommentLoginWall/TimeSavedBubble use
      // for the same job.
      role="region"
      aria-label={t("askV2.review.workspaceLabel")}
    >
      <AskV2ReviewPanes
        abortSignal={review.reviewId}
        left={
          <AskV2ReviewLeftColumn
            review={review}
            workspace={workspace}
            transport={transport}
            compare={compare}
            commentFilter={commentFilter}
            panelComments={panelComments}
            listComments={pinView.ordered}
            commentNumbers={commentNumbers}
            versionMark={versionMarkOf}
            listView={{
              companionsOf: pinView.companionsOf,
              headerControls: pinView.enabled ? (
                <AskV2ReviewPinViewControls
                  mode={pinView.mode}
                  groupBy={pinView.groupBy}
                  showMinor={pinView.showMinor}
                  pinCount={pinView.ordered.length}
                  groupCount={pinView.groupCount}
                  onModeChange={pinView.setMode}
                  onGroupByChange={pinView.setGroupBy}
                  onShowMinorChange={pinView.setShowMinor}
                />
              ) : null,
              severityBands: pinView.enabled && !pinView.grouping,
              showMinor: pinView.enabled
                ? { value: pinView.showMinor, onChange: pinView.setShowMinor }
                : undefined,
              pinGrouping: pinView.enabled
                ? {
                    mode: pinView.mode,
                    groupBy: pinView.groupBy,
                    onModeChange: pinView.setMode,
                    onGroupByChange: pinView.setGroupBy,
                  }
                : undefined,
              pinGroups: pinView.grouping
                ? {
                    groups: pinView.groups,
                    openGroupKey: pinView.openGroupKey,
                    onToggleGroup: groupPins.toggleGroup,
                  }
                : undefined,
            }}
            artworkFiles={artworkFiles}
            contextFiles={contextFiles}
            artworkPaneRef={artworkPaneRef}
            selectedCommentId={selectedCommentId}
            selectComment={selectComment}
            clearComment={clearComment}
            onRemoveCommentContext={handleRemoveCommentContext}
            judgmentFailed={judgmentFailed}
            judgmentPending={isJudgmentPending}
            onJudge={handleJudgeComment}
            feedPresentation={feedPresentation}
            feedActorName={feedActorName}
            feedActorAvatar={feedActorAvatar}
            feedMembers={feedMembers}
            currentUserId={currentUserId}
            authorNameByUserId={authorNameByUserId}
            artifactContext={artifactContext}
            composer={composer}
            readOnly={readOnly}
            commentMode={readOnly ? undefined : artworkFocus.commentMode}
            onBack={workspace.closeReview}
          />
        }
        right={
          <AskV2ReviewArtworkColumn
            review={review}
            workspace={workspace}
            compare={compare}
            pinView={pinView}
            groupPins={groupPins}
            versionOptions={resolvedVersionOptions}
            historyMessages={transport.historyMessages}
            artworkFiles={artworkFiles}
            artworkPaneRef={artworkPaneRef}
            artworkHeaderControls={artworkHeaderControls}
            setArtworkHeaderControls={setArtworkHeaderControls}
            selectedCommentId={selectedCommentId}
            selectComment={selectComment}
            clearComment={clearComment}
            commentNumbers={commentNumbers}
            onCommentModeChange={artworkFocus.commentMode.onChange}
            currentUserId={currentUserId}
            onSubmitHumanComment={
              readOnly ? undefined : handleSubmitHumanComment
            }
          />
        }
      />
    </div>
  );
}
